import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import TopStrip from '../components/TopStrip';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HelpButton from '../components/HelpButton';

import heroBanner from '../assets/images/hero-images/whitelabel.webp';
import heroMobile from '../assets/images/mobile-banners/whitelabel-mobile.webp';
import downloadImg from '../assets/images/webp-images/download-section-img.webp';
import companyPdf from '../assets/pdfs/DDevOps-Company-Profile.pdf';

import invisiblePartner from '../assets/images/whitelabel-page/invisible-partner.png';
import wholesalePricing from '../assets/images/whitelabel-page/wholesale-pricing.png';
import support247 from '../assets/images/whitelabel-page/24_7.png';
import elasticImg from '../assets/images/whitelabel-page/Elastic-establity.png';
import dedicatedAccount from '../assets/images/whitelabel-page/dedicated-account.png';
import riskFree from '../assets/images/whitelabel-page/risk-free.png';

import logo1 from '../assets/images/logo-slide/1.avif';
import logo2 from '../assets/images/logo-slide/2.avif';
import logo3 from '../assets/images/logo-slide/3.avif';
import logo4 from '../assets/images/logo-slide/4.avif';
import logo5 from '../assets/images/logo-slide/5.avif';
import logo6 from '../assets/images/logo-slide/6.avif';
import logo7 from '../assets/images/logo-slide/7.avif';
import logo8 from '../assets/images/logo-slide/8.avif';
import logo9 from '../assets/images/logo-slide/9.avif';

const logoImages = [logo1,logo2,logo3,logo4,logo5,logo6,logo7,logo8,logo9];

const advantages = [
  { img:invisiblePartner, title:'The Invisible Partner',     desc:'We operate in complete silence under your brand. Your clients never know we exist. Your reputation is protected while you deliver enterprise-grade services.' },
  { img:wholesalePricing, title:'Wholesale Pricing Model',   desc:'We charge you wholesale. You set your own retail price. The entire margin is yours. There are no royalties, revenue shares, or hidden platform fees.' },
  { img:support247,       title:'24/7 White-Label Support',  desc:'We provide round-the-clock technical support — always under your brand. Your clients experience seamless support without you staffing a 24/7 team.' },
  { img:elasticImg,       title:'Elastic Scalability',       desc:'As your client base grows, our infrastructure scales with you. You never have to worry about capacity — we handle provisioning dynamically.' },
  { img:dedicatedAccount, title:'Dedicated Account Management', desc:'You get a named account manager from DDevOps who coordinates all operational activities, ensuring nothing falls through the cracks.' },
  { img:riskFree,         title:'Risk-Free Execution',       desc:'We carry the operational risk. Our SLA guarantees cover your commitments to clients. If we fail, we compensate — not your business.' },
];

const sellableServices = [
  { title:'Managed Cloud Hosting',        desc:'Offer fully managed cloud environments on AWS, Azure, or our private infrastructure — all under your agency brand.' },
  { title:'DevOps-as-a-Service',          desc:'Resell our complete DevOps capability — CI/CD pipelines, IaC, and 24/7 operations — as your own branded offering.' },
  { title:'Security & Compliance Audits', desc:'Package our penetration testing, vulnerability scanning, and compliance reporting as a premium service for your clients.' },
  { title:'24/7 Server Monitoring',       desc:'Provide round-the-clock infrastructure monitoring with real-time alerts and incident response under your brand.' },
  { title:'Application Migration',        desc:'Offer seamless cloud migration services. We do the heavy lifting; you take the credit and the contract value.' },
  { title:'Disaster Recovery Planning',   desc:'Help your clients achieve business continuity with documented DR plans, geo-redundant backups, and tested failover procedures.' },
];

const partnerTypes = [
  { title:'TYPE 1 — Agency White-Label', desc:'Digital agencies that want to offer infrastructure and DevOps services without building an internal team. You sell, we deliver, completely under your brand.', items:['No technical headcount required','Full service delivery by DDevOps','Your brand on all deliverables','Monthly white-label reports for your clients'] },
  { title:'TYPE 2 — Hosting Reseller',   desc:'Hosting companies and ISPs that want to expand their product catalog with managed cloud and DevOps services.', items:['Wholesale infrastructure pricing','Resell at your own margin','Co-managed customer portal option','Volume discounts as you scale'] },
];

function WhitelabelSolution() {
  const logoTrackRef = useRef(null);
  const animRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    const track = logoTrackRef.current;
    if (!track) return;
    const run = () => {
      posRef.current -= 0.5;
      if (Math.abs(posRef.current) >= track.scrollWidth / 2) posRef.current = 0;
      track.style.left = posRef.current + 'px';
      animRef.current = requestAnimationFrame(run);
    };
    animRef.current = requestAnimationFrame(run);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <>
      <TopStrip />
      <Navbar />

      {/* HERO */}
      <section style={{ marginTop:'125px' }}>
        <div className="container-fluid p-0">
          <img src={heroBanner} width="1600" height="520" alt="Whitelabel Solution" className="img-fluid w-100 d-none d-md-block" />
          <img src={heroMobile} width="768" height="1000" alt="Whitelabel Solution mobile" className="img-fluid w-100 d-md-none d-block" />
        </div>
      </section>

      {/* INTRO */}
      <section className="my-5">
        <div className="container text-center">
          <h2 className="text-center top-line fw-bold text-uppercase" data-aos="fade-down">
            Your Brand. <span className="txt-color">Our Engine.</span>
          </h2>
          <h3 className="text-center mt-4 fs-6 fs-md-5 text-muted" data-aos="fade-up" style={{ maxWidth:'800px', margin:'0 auto' }}>
            Stop turning away clients because you lack the technical depth. DDevOps becomes your invisible DevOps and infrastructure team, delivering enterprise-grade services that you sell under your own brand — with 100% confidentiality. Your clients see your brand. We stay in the background.
          </h3>
        </div>
      </section>

      {/* WHY WHITE-LABEL */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3 text-uppercase">Why <span className="txt-color">White-Label?</span></h2>
          </div>
          <div className="row g-4">
            {[
              { title:'Instant Expertise', desc:'Skip the 12-month hiring cycle. The moment you partner with DDevOps, you instantly have a senior DevOps and cloud engineering team behind your brand — ready to deliver.' },
              { title:'Pure Profit',       desc:'Your costs are fixed and wholesale. Everything you charge above that is pure margin for your business. No headcount costs, no training expenses, no operational overhead.' },
              { title:'100% Confidential', desc:'Non-disclosure is our default. We operate under strict NDAs. Our engineers communicate as your team. All deliverables are unbranded. Your client relationship is yours alone.' },
            ].map((c,i) => (
              <div className="col-md-4" key={i} data-aos="fade-up" data-aos-delay={i*100}>
                <div className="feature-card h-100">
                  <h5 className="fw-semibold mb-2">{c.title}</h5>
                  <p className="text-muted small">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER TYPES */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-uppercase">Partnership <span className="txt-color">Types</span></h2>
          </div>
          <div className="row g-4">
            {partnerTypes.map((p,i) => (
              <div className="col-md-6" key={i} data-aos={i===0?'fade-right':'fade-left'}>
                <div className="p-4 border rounded shadow-sm h-100">
                  <h5 className="fw-bold mb-1">{p.title}</h5>
                  <p className="text-muted small mb-3">{p.desc}</p>
                  <ul>{p.items.map((item,j) => <li key={j}>{item}</li>)}</ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark text-uppercase">How It <span className="txt-color">Works</span></h2>
          </div>
          <div className="row g-4">
            {[
              { step:'01', title:'You Sign the Client', desc:'Close the deal under your brand. Set the price, define the scope, and send us the project brief.' },
              { step:'02', title:'We Deliver Silently', desc:'Our engineers execute the work. All communication, reports, and deliverables are unbranded — ready for you to forward to your client.' },
              { step:'03', title:'You Take the Credit', desc:'Your client is happy. Your brand grows. Your margin is secure. We remain invisible.' },
            ].map((c,i) => (
              <div className="col-md-4" key={i} data-aos="fade-up" data-aos-delay={i*100}>
                <div className="workflow-card h-100 text-center">
                  <span className="fw-bold text-info fs-1 d-block mb-2">{c.step}</span>
                  <h5 className="fw-bold mb-3">{c.title}</h5>
                  <p className="text-muted small">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANTAGE CARDS (6 image cards) */}
      <section className="py-5 px-3 bg-light">
        <div className="container">
          <h2 className="text-center mb-5 text-uppercase fw-bold top-line">The Whitelabel <span className="text-info">Advantage</span></h2>
          <div className="row g-4">
            {advantages.map((a,i) => (
              <div className="col-md-6 col-lg-4" key={i} data-aos="fade-up" data-aos-delay={i*80}>
                <div className="feature-card h-100 text-center">
                  <img src={a.img} className="img-fluid mb-3" style={{ maxHeight:'100px', objectFit:'contain' }} alt={a.title} />
                  <h5 className="fw-bold mt-3">{a.title}</h5>
                  <p className="text-muted small">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES YOU CAN SELL */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold top-line">Services You Can <span className="txt-color">Sell Today</span></h2>
          </div>
          <div className="row g-4">
            {sellableServices.map((s,i) => (
              <div className="col-md-6 col-lg-4" key={i} data-aos="fade-up" data-aos-delay={i*80}>
                <div className="lifecycle-card h-100">
                  <h5 className="text-info fw-bold mb-2">{s.title}</h5>
                  <p className="text-muted small">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DOWNLOAD */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6" data-aos="fade-right">
              <h4 className="fw-semibold mb-2 top-line">Ready to expand your agency capabilities?</h4>
              <p className="text-muted mb-4">Download our White-Label Partner Starter Kit — includes service catalog, pricing models, NDA template, and onboarding checklist.</p>
              <h2 className="fw-bold mb-3">Your Brand. <span className="txt-color">Backed by Us.</span></h2>
              <a href={companyPdf} target="_blank" rel="noreferrer" className="btn btn-info text-white fw-bold px-4 py-3">Download Partner Starter Kit</a>
            </div>
            <div className="col-lg-6 text-center" data-aos="fade-left">
              <img src={downloadImg} loading="lazy" alt="Partner Starter Kit" className="img-fluid floating-icon mt-4" style={{ maxHeight:'350px', objectFit:'contain' }} />
            </div>
          </div>
        </div>
      </section>

      {/* DARK CTA */}
      <section className="py-5 bg-dark text-white">
        <div className="container text-center">
          <h2 className="fw-bold mb-2">Start Reselling <span className="text-info">DevOps</span> Under Your Brand Today.</h2>
          <p className="text-white-50 mb-4">No setup fees. No long-term commitment for the first 30 days. Just results.</p>
          <Link to="/contact" className="btn btn-info text-white px-5 py-3 fw-bold">Become a White-Label Partner</Link>
        </div>
      </section>

      {/* LOGO SLIDER */}
      <section className="logo-slider py-1 bg-white overflow-hidden mt-5">
        <h2 className="text-center text-uppercase top-line">The <span className="text-info fw-semibold">DDevOps</span> Toolkit</h2>
        <div className="container" style={{ margin:'1rem auto' }}>
          <div className="logo-wrapper">
            <div className="logo-track" ref={logoTrackRef} style={{ position:'relative' }}>
              {[...logoImages,...logoImages].map((img,i) => (
                <img key={i} src={img} loading="lazy" className="slide-logo-img" alt={`Logo ${(i%9)+1}`} height="60" />
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <HelpButton />
    </>
  );
}

export default WhitelabelSolution;
